import { createContext, PropsWithChildren, useMemo } from "react";
import useEvent from "react-use-event-hook";

import { AxiosError } from "axios";

import { ApiInterceptorError } from "@classes/ApiInterceptorError";
import { LOCAL_STORAGE } from "@constants";
import { env } from "@settings";

import { useApiFeatureFlags } from "@hooks/api/common";
import { decodeXorCipher, encodeXorCipher } from "@utils/encode";

import { FeatureFlagsKeys, mapFeatureFlagsToBoolean } from "./flags";

export type FeatureFlagsType = {
  [key in FeatureFlagsKeys]: boolean;
};

type FeatureFlagsContextType = {
  featureFlags: Partial<FeatureFlagsType>;
  fetchFeatureFlags: () => Promise<void>;
};

export const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  featureFlags: {},
  fetchFeatureFlags: () => Promise.resolve(),
});

export const FeatureFlagsProvider = ({ children }: PropsWithChildren) => {
  const [fetchFeatureFlags, { data: dataFeatureFlags }] = useApiFeatureFlags();

  const featureFlags = useMemo(() => {
    let flags = mapFeatureFlagsToBoolean(dataFeatureFlags);

    const featureFlagsStored = localStorage.getItem(
      LOCAL_STORAGE.FEATURE_FLAGS,
    );

    if (featureFlagsStored) {
      flags = JSON.parse(
        encodeXorCipher(featureFlagsStored, env.VITE_FEATURE_FLAG_KEY!),
      ) as FeatureFlagsType;
    }

    return flags;
  }, [dataFeatureFlags]);

  const fetch = useEvent(async () => {
    try {
      const { data: featureFlagsData } = await fetchFeatureFlags();

      if (featureFlagsData) {
        const featureFlagsUnstored = mapFeatureFlagsToBoolean(featureFlagsData);
        localStorage.setItem(
          LOCAL_STORAGE.FEATURE_FLAGS,
          decodeXorCipher(
            JSON.stringify(featureFlagsUnstored),
            env.VITE_FEATURE_FLAG_KEY!,
          ),
        );
      }
    } catch (e) {
      if (
        e instanceof ApiInterceptorError &&
        e.sourceError instanceof AxiosError &&
        !!e.sourceError?.response
      ) {
        console.error(e.sourceError.response);
        throw new Error(
          "Ocorreu um erro ao recuperar as feature flags do sistema",
        );
      }
    }
  });

  const contextValues: FeatureFlagsContextType = useMemo(
    () => ({
      featureFlags,
      fetchFeatureFlags: fetch,
    }),
    [featureFlags, fetch],
  );

  return (
    <FeatureFlagsContext.Provider value={contextValues}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
